import type { CanvasJSON } from '../types';

// A fitted image (cover/contain) fills a box. Templates encode that box as the object's declared
// width/height, but the model reads an image's size from its native pixels — so the box is lost on
// round-trip. Derive an explicit imageBox before load so it survives; the fit scale stays derived.
export function deriveImageBoxes(json: CanvasJSON): CanvasJSON {
  if (!json?.objects) return json;
  return { ...json, objects: mapDeep(json.objects) };
}

function mapDeep<T extends Record<string, unknown>>(objects: T[]): T[] {
  return objects.map(obj => {
    const next = withImageBox(obj);
    return Array.isArray(next.objects) ? { ...next, objects: mapDeep(next.objects as T[]) } : next;
  });
}

function withImageBox<T extends Record<string, unknown>>(obj: T): T {
  const o = obj as {
    type?: string;
    imageFit?: string;
    imageBox?: unknown;
    width?: number;
    height?: number;
    scaleX?: number;
    scaleY?: number;
  };
  const isImage = o.type === 'image' || o.type === 'Image' || o.type === 'FabricImage';
  if (!isImage || !o.imageFit || o.imageFit === 'none' || o.imageBox) return obj;

  const width = (o.width ?? 0) * (o.scaleX ?? 1);
  const height = (o.height ?? 0) * (o.scaleY ?? 1);
  if (width <= 0 || height <= 0) return obj;

  return { ...obj, imageBox: { width, height } };
}
